import React, { useState, useRef } from 'react'
import { Upload, X, File, FileText, Image, FileSpreadsheet } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { useAlert } from '@/components/ui/Alert/exports'

export interface FileItem {
  id: string                    // 고유 ID
  file: File                   // 실제 파일 객체
  name: string                 // 파일명
  size: number                // 파일 크기
  type: string                // MIME 타입
  uploadedAt: Date            // 업로드 시간
  status: 'pending' | 'uploading' | 'completed' | 'error'
  progress?: number           // 업로드 진행률 (0-100)
  error?: string             // 에러 메시지
}

interface FileUploadSectionProps {
  files: FileItem[]
  onChange: (files: FileItem[]) => void
  maxFiles?: number           // 최대 파일 개수 (기본: 100)
  maxFileSize?: number        // 최대 파일 크기 MB (기본: 1000MB)
  disabled?: boolean
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  files,
  onChange,
  maxFiles = 100,
  maxFileSize = 1000,
  disabled = false
}) => {
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError } = useAlert()

  // 드래그 이벤트 처리
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) setIsDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)

    if (disabled) return

    const droppedFiles = Array.from(e.dataTransfer.files)
    handleFilesAdd(droppedFiles)
  }

  // 파일 선택 버튼 클릭
  const handleButtonClick = () => {
    if (!disabled) {
      fileInputRef.current?.click()
    }
  }

  // 파일 선택 이벤트 처리
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      handleFilesAdd(selectedFiles)
      // 동일한 파일 재선택 가능하도록 초기화
      e.target.value = ''
    }
  }

  // 파일 추가 처리
  const handleFilesAdd = (newFiles: File[]) => {
    // 파일 개수 검증
    if (files.length + newFiles.length > maxFiles) {
      showError(`최대 ${maxFiles}개까지 업로드 가능합니다`)
      return
    }

    // 각 파일 검증 및 FileItem 생성
    const validFileItems: FileItem[] = []

    for (const file of newFiles) {
      // 파일 크기 검증
      if (file.size > maxFileSize * 1024 * 1024) {
        showError(`${file.name}은(는) ${maxFileSize}MB를 초과합니다`)
        continue
      }

      // 중복 파일명 체크 (선택적)
      const isDuplicate = files.some(f => f.name === file.name)
      if (isDuplicate) {
        showError(`${file.name}은(는) 이미 추가된 파일입니다`)
        continue
      }

      // FileItem 객체 생성
      validFileItems.push({
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date(),
        status: 'completed',
        progress: 100
      })
    }

    if (validFileItems.length > 0) {
      onChange([...files, ...validFileItems])
    }
  }

  // 파일 삭제 처리
  const handleFileDelete = (fileId: string) => {
    onChange(files.filter(f => f.id !== fileId))
  }

  // 파일 크기 포맷
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
  }

  // 파일 아이콘 선택
  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return <Image className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
    }
    if (type.includes('pdf')) {
      return <FileText className="w-5 h-5" style={{ color: designTokens.colors.error }} />
    }
    if (type.includes('spreadsheet') || type.includes('excel')) {
      return <FileSpreadsheet className="w-5 h-5" style={{ color: designTokens.colors.success }} />
    }
    return <File className="w-5 h-5" style={{ color: designTokens.colors.gray[4] }} />
  }

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div
        className="pb-2 border-b"
        style={{
          borderBottomColor: '#EBEBEB',
          borderBottomWidth: '1px',
          padding: '7px 0px'
        }}
      >
        <h3
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '18px',
            fontWeight: 700,
            color: designTokens.colors.gray[6]
          }}
        >
          첨부파일
        </h3>
      </div>

      {/* Upload Area */}
      <div style={{ padding: '0px 4px' }}>
        {/* File List */}
        {files.length > 0 && (
          <div className="my-4 space-y-2">
            <div
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                fontWeight: 600,
                color: designTokens.colors.gray[5]
              }}
            >
              업로드된 파일 ({files.length}개)
            </div>

            <div className="space-y-2">
              {files.map(file => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 border rounded-lg transition-colors hover:bg-gray-50"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    backgroundColor: designTokens.colors.white
                  }}
                >
                  <div className="flex items-center gap-3 flex-1">
                    {getFileIcon(file.type)}
                    <div className="flex-1 min-w-0">
                      <div
                        className="truncate"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '14px',
                          fontWeight: 500,
                          color: designTokens.colors.gray[6]
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          fontSize: '12px',
                          color: designTokens.colors.gray[4]
                        }}
                      >
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  </div>

                  {/* Progress bar (for future API integration) */}
                  {file.status === 'uploading' && file.progress !== undefined && (
                    <div className="w-32 mr-3">
                      <div
                        className="h-2 rounded overflow-hidden"
                        style={{ backgroundColor: designTokens.colors.gray[1] }}
                      >
                        <div
                          className="h-full rounded transition-all duration-300"
                          style={{
                            width: `${file.progress}%`,
                            backgroundColor: designTokens.colors.primary.DEFAULT
                          }}
                        />
                      </div>
                      <div
                        className="text-xs mt-1 text-center"
                        style={{
                          fontFamily: designTokens.typography.fontFamily.sans[0],
                          color: designTokens.colors.gray[4]
                        }}
                      >
                        {file.progress}%
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {file.status === 'error' && (
                    <span
                      className="text-sm mr-3"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        color: designTokens.colors.error
                      }}
                    >
                      {file.error}
                    </span>
                  )}

                  {/* Delete button */}
                  {file.status === 'completed' && (
                    <button
                      type="button"
                      onClick={() => handleFileDelete(file.id)}
                      disabled={disabled}
                      className="p-2 rounded hover:bg-red-50 transition-colors"
                      style={{
                        color: designTokens.colors.error
                      }}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-8
            transition-all duration-200 cursor-pointer
            ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            borderColor: isDragActive
              ? designTokens.colors.primary.DEFAULT
              : designTokens.colors.gray[2],
            backgroundColor: isDragActive
              ? designTokens.colors.primary.light + '10'
              : designTokens.colors.white
          }}
          onClick={handleButtonClick}
        >
          <div className="flex flex-col items-center justify-center space-y-3">
            <Upload
              className="w-10 h-10"
              style={{
                color: isDragActive
                  ? designTokens.colors.primary.DEFAULT
                  : designTokens.colors.gray[3]
              }}
            />

            <div className="text-center">
              <p
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  fontWeight: 600,
                  color: designTokens.colors.gray[6]
                }}
              >
                파일을 드래그하여 업로드하거나 선택해주세요.
              </p>
              <button
                type="button"
                disabled={disabled}
                className="mt-2 px-4 py-2 bg-white border rounded-md hover:bg-gray-50"
                style={{
                  borderColor: designTokens.colors.gray[2],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '14px',
                  fontWeight: 600,
                  color: designTokens.colors.primary.DEFAULT
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleButtonClick()
                }}
              >
                파일 선택
              </button>
            </div>

            <p
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '12px',
                color: designTokens.colors.gray[4]
              }}
            >
              최대 {maxFiles}개, 각 파일 최대 {maxFileSize}MB
            </p>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}

export default FileUploadSection